"use client";

import React from "react";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const chartData = [{
  month: "Jan",
  movement: 1.2
}, {
  month: "Feb",
  movement: 2.4
}, {
  month: "Mar",
  movement: -0.8
}, {
  month: "Apr",
  movement: -1.5
}, {
  month: "May",
  movement: 0.5
}, {
  month: "Jun",
  movement: 3.2
}];
const chartConfig = {
  movement: {
    label: "Movement (%)"
  }
} satisfies ChartConfig;
function SubscriptionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-4 bg-white/10 border border-white/20 rounded-sm">
        <p className="text-white font-sans text-[16px]">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 w-full max-w-xl">
      <div className="flex-1 relative">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-0">
              <FormControl>
                <Input placeholder="Enter your email address" className="h-12 rounded-sm bg-white/5 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-white/30 focus-visible:border-white font-sans text-[16px]" {...field} />
              </FormControl>
              <FormMessage className="text-red-400 mt-1.5 absolute text-sm" />
            </FormItem>} />
      </div>
      <Button type="submit" disabled={isSubmitting} className="h-12 px-8 rounded-sm bg-white text-black hover:bg-white/90 font-medium shrink-0 transition-colors">
        {isSubmitting ? "Subscribing..." : "Subscribe"}
      </Button>
    </Form>;
}
export default function MonthlyMovementSubscription() {
  return <section id="monthly-movement-subscription" className="bg-black text-white py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-16">
          {}
          <div className="lg:col-span-6 flex flex-col justify-center">
            <h2 className="font-default text-[32px] font-semibold text-white mb-6 leading-tight">
              Monthly Market Shifts
            </h2>
            <div className="space-y-6">
              <p className="font-sans text-[16px] text-white/90 leading-relaxed">
                Our latest analysis indicates a significant shift in market
                dynamics this month. The data reflects a complex interplay of
                global economic pressures and localized supply constraints.
                While some sectors show remarkable resilience, the overall trend
                suggests a cautious approach is warranted for the upcoming
                quarter.
              </p>
              <p className="font-sans text-[16px] text-white/90 leading-relaxed">
                The most notable movements were observed in the mid-tier
                segments, where volatility reached a six-month high. Stay
                informed with our detailed monthly breakdowns to navigate these
                fluctuations effectively.
              </p>
            </div>
          </div>

          {}
          <div className="lg:col-span-6 flex items-center justify-center">
            <div className="w-full p-6 bg-white/5 border border-white/10 rounded-lg">
              <ChartContainer config={chartConfig} className="h-[320px] w-full">
                <BarChart data={chartData} margin={{
                top: 20,
                right: 0,
                left: -20,
                bottom: 0
              }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="month" stroke="rgba(255,255,255,0.5)" fontSize={12} tickLine={false} axisLine={false} dy={10} />
                  <YAxis stroke="rgba(255,255,255,0.5)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `${value}%`} dx={-10} />
                  <ChartTooltip cursor={{
                  fill: "rgba(255,255,255,0.05)"
                }} content={<ChartTooltipContent className="bg-black border-white/20 text-white" formatter={value => <span className="font-mono font-medium">
                            {Number(value) > 0 ? "+" : ""}
                            {value}%
                          </span>} />} />
                  <Bar dataKey="movement" radius={[2, 2, 0, 0]} maxBarSize={50}>
                    {chartData.map((entry, index) => <Cell key={`cell-${index}`} data-index={index} fill={entry.movement >= 0 ? "#10b981" : "#e11d48"} />)}
                  </Bar>
                </BarChart>
              </ChartContainer>
            </div>
          </div>
        </div>

        {}
        <div className="pt-12 border-t border-white/10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="w-full lg:w-auto flex-1">
            <h3 className="font-default text-xl font-medium text-white mb-4 lg:hidden">
              Subscribe for Updates
            </h3>
            <FormProvider formId="monthly-movement-subscription-form" sectionName="Monthly Movement" defaultValues={{
            email: ""
          }} submitText="Subscribe" successMessage="Thank you for subscribing to our monthly updates.">
              <SubscriptionForm />
            </FormProvider>
          </div>
          
          <div className="shrink-0 w-full lg:w-auto flex justify-start lg:justify-end">
            <Link to="/ContactPage" className="inline-flex items-center justify-center h-12 px-8 rounded-sm border border-white text-white hover:bg-white/10 font-medium transition-colors w-full sm:w-auto">
              Media Enquiry
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>;
}